<template>
  <div v-if="org">
    <b-card>
      <div class="d-flex align-items-center">
        <h2 class="d-inline">
          {{ org.name }}
        </h2>
        <template v-if="canEdit">
          <b-btn
            v-b-modal.org-details
            v-b-tooltip.hover="'edit details'"
            class="mr-auto"
            variant="link"
          >
            <i class="icon-pencil" />
          </b-btn>
          <b-link
            v-if="hasPermission('audit.access')"
            :href="`/admin/audit-log?organization_id=${org.id}`"
            target="_blank"
            class="mr-2 btn btn-outline-primary"
          >
            <i class="icon-list"></i>
            Audit Log
          </b-link>
          <b-btn
            v-if="canSSO"
            v-b-modal.org-sso
            class="mr-2"
            variant="outline-primary"
          >
            <i class="icon-lock" /> SSO
          </b-btn>
          <b-btn v-b-modal.org-logins class="mr-2" variant="outline-primary">
            <i class="icon-list" /> Logins
          </b-btn>
          <b-btn v-b-modal.org-settings class="mr-2" variant="outline-primary">
            <i class="icon-lock" /> Security
          </b-btn>
          <b-btn v-b-modal.org-connectivity-config variant="outline-primary">
            <i class="icon-settings" /> Config
          </b-btn>
          <b-btn
            v-if="canDelete"
            v-b-modal.delete-org
            v-b-tooltip.hover="'Delete organization'"
            variant="outline-danger"
            class="ml-2"
          >
            <i class="icon-trash" />
          </b-btn>

          <template v-if="org.id">
            <OrgConnectivityConfigModal :org="org" @updated="getDetails" />
            <OrgDeleteModal :org="org" @updated="$emit('updated')" />
            <OrgDetailsModal :org="org" @updated="getDetails" />
            <OrgLoginsModal :org="org" />
            <OrgSettingsModal :org="org" @updated="$emit('updated')" />
            <OrgSSOModal :org="org" @updated="$emit('updated')" />
          </template>
        </template>
      </div>

      <div>{{ org.members.length | pluralize("Member") }}</div>
      <div class="mb-2 small">Org ID: {{ org.id }}</div>

      <b-tabs>
        <b-tab title="Members" active>
          <OrgMembersTable
            v-if="org.id"
            :can-edit="canEdit"
            :is-owner="isOrgOwner"
            :org="org"
            @updated="getDetails"
          />
        </b-tab>

        <b-tab title="Roles">
          <OrgRolesTable
            v-if="org.id"
            :org="org"
            :can-edit="canEdit"
            @updated="getDetails"
          />
        </b-tab>

        <b-tab v-if="canManageLicenses" title="Licenses">
          <LicensesManage
            :licenses="org.licenses"
            :assign-emails="memberEmails"
          />
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import API from "@/api";

import LicensesManage from "../licenses/LicensesManage.vue";
import OrgConnectivityConfigModal from "./OrgConnectivityConfigModal.vue";
import OrgDeleteModal from "./OrgDeleteModal.vue";
import OrgDetailsModal from "./OrgDetailsModal.vue";
import OrgLoginsModal from "./OrgLoginsModal.vue";
import OrgMembersTable from "./OrgMembersTable.vue";
import OrgRolesTable from "./OrgRolesTable.vue";
import OrgSettingsModal from "./OrgSettingsModal.vue";
import OrgSSOModal from "./OrgSSOModal.vue";

export default {
  components: {
    LicensesManage,
    OrgConnectivityConfigModal,
    OrgDeleteModal,
    OrgDetailsModal,
    OrgLoginsModal,
    OrgMembersTable,
    OrgRolesTable,
    OrgSettingsModal,
    OrgSSOModal
  },

  props: {
    orgId: Number
  },

  data: () => ({
    org: {
      licenses: [],
      members: [],
      roles: [],
      settings: {}
    }
  }),

  computed: {
    ...mapGetters(["isAdmin", "hasPermission"]),
    ...mapState(["user"]),

    canDelete() {
      return this.isOrgOwner || this.hasPermission("org.delete");
    },

    canEdit() {
      return this.isOrgAdmin || this.hasPermission("org.edit");
    },

    canManageLicenses() {
      const hasLicenses = this.org.licenses && this.org.licenses.length;
      const hasPermission = this.hasPermission("license.assign");
      return hasLicenses && (this.isOrgAdmin || hasPermission);
    },

    canSSO() {
      if (this.isAdmin) return true;
      const { capabilities, data } = this.user.license;
      return (
        capabilities["sso-support"] ||
        (data && data.extras && data.extras["sso-support"])
      );
    },

    isOrgAdmin() {
      if (this.isAdmin) return false;
      const me = this.org.members.find(m => m.id === this.user.id);
      if (!me) return false;
      const roles = me.roles ? me.roles.split(",") : [];
      return ["Admin", "Owner"].some(r => roles.includes(r));
    },

    isOrgOwner() {
      if (this.isAdmin) return false;
      const me = this.org.members.find(m => m.id === this.user.id);
      if (!me) return false;
      const roles = me.roles ? me.roles.split(",") : [];
      return roles.includes("Owner");
    },

    memberEmails() {
      return this.org.members.map(m => m.email);
    }
  },

  watch: {
    orgId: {
      handler: "getDetails",
      immediate: true
    }
  },

  methods: {
    async getDetails() {
      const { success, results } = await API.getOrgDetails(this.orgId);
      if (!success) return;
      this.org = results;
    }
  }
};
</script>
